import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { StringHelper } from '@newroom-connect/library/helpers';
import { AuthService, SessionService, SKIP_SESSION_REFRESH } from '@newroom-connect/library/services';
import { AuthError, AuthErrorName } from '@newroom-connect/library/errors';

/**
 * HTTP interceptor to apply an access token to each request, if an access token is available.
 */
@Injectable()
export class HttpAuthInterceptor implements HttpInterceptor {
  private readonly sessionService = inject(SessionService);
  private readonly authService = inject(AuthService);

  /**
   * Intercept an HTTP request where the session is refreshed if needed and
   * the access token is applied to the request in the `Authorization` header.
   *
   * @param request The request to be intercepted.
   * @param next The HTTP handler to pass the intercepted request to.
   *
   * @returns Observable of the intercepted request.
   */
  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Check if session refresh should be skipped via context token
    const skipRefresh = request?.context?.get(SKIP_SESSION_REFRESH);

    // Conditionally refresh session or skip it
    const sessionRefresh$ = skipRefresh
      ? of(null)
      : this.authService.refreshSessionIfNeeded(request.url);

    return sessionRefresh$.pipe(
      catchError((error) => {
        if (error instanceof AuthError && error.message.includes(AuthErrorName.SESSION_EXPIRED)) {
          this.sessionService.clearUserSession();
          return throwError(() => error);
        }

        return throwError(() => error);
      }),
      switchMap(() => {
        const userSession = this.sessionService.getUserSession();
        const accessToken = userSession?.getAccessToken();
        const sessionId = userSession?.getSessionId();

        let headers = request.headers;

        if (accessToken) {
          headers = headers.set('Authorization', accessToken);
        }

        if (StringHelper.isTransportableString(sessionId)) {
          headers = headers.set('x-session-id', sessionId);
        }

        const modifiedRequest = request.clone({ headers });

        return next.handle(modifiedRequest);
      }));
  }
}
